/** Shared building blocks for menu screens. */
import type { GradeCategory, GradeReport, MatchConfig } from '../../core/contracts';
import { h } from '../dom';
import { CATEGORY_LABELS, LETTER_COLORS, TIME_LABELS, WEATHER_LABELS, barColor } from '../format';
import { CATEGORY_ORDER } from '../../meta/share';

export interface ScreenInstance {
  el: HTMLElement;
  /** Overlay screens keep the match visible behind a dimmed backdrop. */
  overlay?: boolean;
  /** Return true if handled. */
  onKey?(e: KeyboardEvent): boolean;
  /** Called after the element is attached (for entrance animations / focus). */
  mounted?(): void;
  destroy?(): void;
}

export function screenHeader(title: string, opts: { kicker?: string; onBack?: () => void; right?: Node } = {}): HTMLElement {
  return h('header', { class: 'sd-sh' }, [
    opts.onBack ? h('button', { class: 'sd-back', attrs: { type: 'button' }, on: { click: opts.onBack } }, [h('span', { class: 'sd-back__arrow', text: '←' }), h('span', { text: 'BACK' }), h('kbd', { text: 'ESC' })]) : null,
    h('div', { class: 'sd-sh__titles' }, [
      opts.kicker ? h('div', { class: 'sd-sh__kicker', text: opts.kicker }) : null,
      h('h1', { class: 'sd-sh__title', text: title }),
    ]),
    opts.right ?? null,
  ]);
}

export interface Segmented<T extends string> { el: HTMLElement; value(): T; set(v: T): void }

export function segmented<T extends string>(options: { value: T; label: string; sub?: string }[], initial: T, onChange: (v: T) => void, cls = ''): Segmented<T> {
  let cur = initial;
  const btns = options.map((o) => h('button', {
    class: 'sd-seg-opt', attrs: { type: 'button', role: 'radio', 'aria-checked': String(o.value === cur) },
    on: { click: () => { set(o.value); onChange(o.value); } },
  }, [h('span', { class: 'sd-seg-opt__label', text: o.label }), o.sub ? h('span', { class: 'sd-seg-opt__sub', text: o.sub }) : null]));
  const thumb = h('span', { class: 'sd-segctl__thumb' });
  const el = h('div', { class: `sd-segctl ${cls}`, attrs: { role: 'radiogroup' } }, [thumb, ...btns]);
  el.style.setProperty('--n', String(options.length));
  function set(v: T) {
    cur = v;
    const i = options.findIndex((o) => o.value === v);
    el.style.setProperty('--i', String(Math.max(0, i)));
    btns.forEach((b, j) => { b.classList.toggle('is-on', j === i); b.setAttribute('aria-checked', String(j === i)); });
  }
  set(initial);
  return { el, value: () => cur, set };
}

export function toggleSwitch(initial: boolean, onChange: (v: boolean) => void): { el: HTMLElement; set(v: boolean): void } {
  let on = initial;
  const el = h('button', { class: 'sd-switch', attrs: { type: 'button', role: 'switch' }, on: { click: () => { set(!on); onChange(on); } } }, [
    h('span', { class: 'sd-switch__track' }, [h('span', { class: 'sd-switch__knob' })]),
    h('span', { class: 'sd-switch__label' }),
  ]);
  function set(v: boolean) {
    on = v;
    el.classList.toggle('is-on', v);
    el.setAttribute('aria-checked', String(v));
    el.querySelector('.sd-switch__label')!.textContent = v ? 'ON' : 'OFF';
  }
  set(initial);
  return { el, set };
}

/** Broadcast-style fader (0..1). Drag, click, wheel and arrow keys. */
export function fader(initial: number, onChange: (v: number) => void): { el: HTMLElement; set(v: number): void } {
  let v = initial;
  const fill = h('span', { class: 'sd-fader__fill' });
  const knob = h('span', { class: 'sd-fader__knob' }, [h('span', { class: 'sd-fader__grip' })]);
  const ticks = h('span', { class: 'sd-fader__ticks' }, Array.from({ length: 11 }, () => h('i')));
  const track = h('span', { class: 'sd-fader__track' }, [ticks, fill, knob]);
  const readout = h('span', { class: 'sd-fader__val' });
  const el = h('div', { class: 'sd-fader', attrs: { role: 'slider', tabindex: 0, 'aria-valuemin': 0, 'aria-valuemax': 100 } }, [track, readout]);
  function set(nv: number, emit = false) {
    v = Math.max(0, Math.min(1, nv));
    el.style.setProperty('--v', String(v));
    readout.textContent = `${Math.round(v * 100)}%`;
    el.setAttribute('aria-valuenow', String(Math.round(v * 100)));
    if (emit) onChange(v);
  }
  const fromEvent = (e: PointerEvent) => {
    const r = track.getBoundingClientRect();
    set((e.clientX - r.left) / r.width, true);
  };
  track.addEventListener('pointerdown', (e) => {
    track.setPointerCapture(e.pointerId); fromEvent(e); el.classList.add('is-drag');
    const move = (ev: PointerEvent) => fromEvent(ev);
    const up = () => { el.classList.remove('is-drag'); track.removeEventListener('pointermove', move); track.removeEventListener('pointerup', up); };
    track.addEventListener('pointermove', move); track.addEventListener('pointerup', up);
  });
  el.addEventListener('wheel', (e) => { e.preventDefault(); set(v - Math.sign(e.deltaY) * 0.05, true); }, { passive: false });
  el.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { set(v + 0.05, true); e.preventDefault(); e.stopPropagation(); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { set(v - 0.05, true); e.preventDefault(); e.stopPropagation(); }
  });
  set(initial);
  return { el, set: (nv) => set(nv) };
}

const WEATHER_ICON: Record<MatchConfig['weather'], string> = { clear: '☀', overcast: '☁', rain: '☂', snow: '❄', fog: '≋' };

/** Fixture card: two angled team slabs meeting in the middle. */
export function fixtureCard(cfg: MatchConfig, opts: { selected?: boolean; onClick?: () => void; big?: boolean } = {}): HTMLElement {
  const dramaPips = h('span', { class: 'sd-fx__drama' }, Array.from({ length: 5 }, (_, i) => h('i', { class: i < Math.round(cfg.drama * 5) ? 'on' : '' })));
  const el = h(opts.onClick ? 'button' : 'div', {
    class: `sd-fx ${opts.big ? 'sd-fx--big' : ''} ${opts.selected ? 'is-selected' : ''}`,
    attrs: opts.onClick ? { type: 'button' } : {},
    on: opts.onClick ? { click: opts.onClick } : {},
  }, [
    h('div', { class: 'sd-fx__slabs' }, [
      h('div', { class: 'sd-fx__slab sd-fx__slab--home', style: { background: `linear-gradient(115deg, ${cfg.home.primaryColor} 0 72%, ${cfg.home.secondaryColor} 72% 78%, ${cfg.home.primaryColor} 78%)` } }, [
        h('span', { class: 'sd-fx__short', text: cfg.home.shortName }),
      ]),
      h('div', { class: 'sd-fx__vs', text: 'VS' }),
      h('div', { class: 'sd-fx__slab sd-fx__slab--away', style: { background: `linear-gradient(115deg, ${cfg.away.primaryColor} 0 22%, ${cfg.away.secondaryColor} 22% 28%, ${cfg.away.primaryColor} 28%)` } }, [
        h('span', { class: 'sd-fx__short', text: cfg.away.shortName }),
      ]),
    ]),
    h('div', { class: 'sd-fx__names' }, [h('span', { text: cfg.home.name }), h('span', { text: cfg.away.name })]),
    h('div', { class: 'sd-fx__meta' }, [
      h('span', { class: 'sd-fx__wx', text: `${WEATHER_ICON[cfg.weather]} ${WEATHER_LABELS[cfg.weather]}` }),
      h('span', { text: TIME_LABELS[cfg.timeOfDay] }),
      h('span', { class: 'sd-fx__dramawrap' }, [h('span', { text: 'DRAMA' }), dramaPips]),
    ]),
    h('div', { class: 'sd-fx__foot' }, [h('span', { class: 'sd-fx__venue', text: cfg.venue }), h('span', { class: 'sd-fx__seed', text: `SEED ${cfg.seed}` })]),
    opts.selected !== undefined ? h('span', { class: 'sd-fx__lamp' }) : null,
  ]);
  return el;
}

export function letterBadge(letter: GradeReport['letter'], cls = ''): HTMLElement {
  const el = h('span', { class: `sd-letter ${cls}`, text: letter });
  el.style.setProperty('--lc', LETTER_COLORS[letter]);
  return el;
}

export function categoryBars(byCategory: Record<GradeCategory, number>, opts: { stagger?: number } = {}): HTMLElement {
  return h('div', { class: 'sd-cbars' }, CATEGORY_ORDER.map((c, i) => {
    const v = Math.round(byCategory[c] ?? 0);
    const fill = h('span', { class: 'sd-cbar__fill', style: { background: barColor(v), animationDelay: `${(opts.stagger ?? 0.07) * i}s` } });
    fill.style.setProperty('--w', String(v / 100));
    return h('div', { class: 'sd-cbar' }, [
      h('span', { class: 'sd-cbar__label', text: CATEGORY_LABELS[c] }),
      h('span', { class: 'sd-cbar__track' }, [fill]),
      h('span', { class: 'sd-cbar__val', text: String(v) }),
    ]);
  }));
}

export function statBlock(label: string, value: string | Node, sub?: string): HTMLElement {
  return h('div', { class: 'sd-stat' }, [
    h('div', { class: 'sd-stat__label', text: label }),
    h('div', { class: 'sd-stat__value' }, [value]),
    sub ? h('div', { class: 'sd-stat__sub', text: sub }) : null,
  ]);
}

/** Arrow-key navigation within a list of buttons. */
export function listNav(buttons: () => HTMLElement[], e: KeyboardEvent, axis: 'y' | 'x' = 'y'): boolean {
  const list = buttons();
  if (!list.length) return false;
  const prev = axis === 'y' ? 'ArrowUp' : 'ArrowLeft';
  const next = axis === 'y' ? 'ArrowDown' : 'ArrowRight';
  if (e.key !== prev && e.key !== next) return false;
  const i = list.indexOf(document.activeElement as HTMLElement);
  const j = i < 0 ? 0 : (i + (e.key === next ? 1 : -1) + list.length) % list.length;
  list[j]!.focus();
  return true;
}
